import { FileArrowUp, UploadSimpleIcon, X } from "@phosphor-icons/react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUploadApartments } from "@/api/districts";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface ApartmentCsvUploadProps {
	districtId: number;
	onSuccess: () => void;
}

export function ApartmentCsvUpload({
	districtId,
	onSuccess,
}: ApartmentCsvUploadProps) {
	const { t } = useTranslation();
	const [file, setFile] = useState<File | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const upload = useUploadApartments();

	function handleSubmit() {
		if (!file) return;
		upload.mutate(
			{ district_id: districtId, file },
			{
				onSuccess: () => {
					toast.success(t("apartmentImport.success"));
					setFile(null);
					if (inputRef.current) inputRef.current.value = "";
					onSuccess();
				},
				onError: () => {
					toast.error(t("apartmentImport.error"));
				},
			},
		);
	}

	function handleClear() {
		setFile(null);
		if (inputRef.current) inputRef.current.value = "";
	}

	const handleDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const dropped = e.dataTransfer.files[0];
		if (dropped) setFile(dropped);
	}, []);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback(() => setIsDragging(false), []);

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center justify-between">
				<Label>{t("apartmentImport.fileLabel")}</Label>
				<a
					href="/CSV_EXAMPLE_APARTMENTS_IMPORT.csv"
					download
					className="text-xs text-muted-foreground underline hover:text-foreground"
				>
					{t("apartmentImport.downloadTemplate")}
				</a>
			</div>
			{file ? (
				<div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm">
					<FileArrowUp
						aria-hidden="true"
						className="size-4 shrink-0 text-muted-foreground"
					/>
					<span className="flex-1 truncate">{file.name}</span>
					<button
						type="button"
						aria-label="Remove file"
						onClick={handleClear}
						className="shrink-0 text-muted-foreground hover:text-foreground"
					>
						<X aria-hidden="true" className="size-4" />
					</button>
				</div>
			) : (
				<button
					type="button"
					onClick={() => inputRef.current?.click()}
					onDrop={handleDrop}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					className={`flex flex-col items-center gap-2 rounded-md border-2 border-dashed px-4 py-6 text-center text-sm transition-colors ${
						isDragging
							? "border-primary bg-primary/5 text-primary"
							: "border-muted-foreground/25 text-muted-foreground hover:border-primary/50 hover:text-foreground"
					}`}
				>
					<UploadSimpleIcon aria-hidden="true" className="size-6" />
					<span>
						{isDragging
							? t("apartmentImport.dragActive")
							: t("apartmentImport.dragDrop")}
					</span>
				</button>
			)}
			<input
				ref={inputRef}
				type="file"
				accept=".csv"
				className="hidden"
				onChange={(e) => setFile(e.target.files?.[0] ?? null)}
			/>
			{file && (
				<Button
					onClick={handleSubmit}
					disabled={upload.isPending}
					className="w-full"
				>
					{upload.isPending
						? t("apartmentImport.uploading")
						: t("apartmentImport.upload")}
				</Button>
			)}
		</div>
	);
}
